import ReactGA from 'react-ga4';
import { observable, action } from 'mobx';

import { inWhitelabel, getWhitelabelConfig } from 'core/whitelabel';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';

import { hasSpaceFreeTrial } from 'utils/freeTrials';

import { conf as globalConf } from 'apps/config.yaml';

import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

let site = globalConf.siteName;
if (inWhitelabel()) {
  const conf = getWhitelabelConfig();
  site = conf.title || 'Whitelabel';
}

const defaultFBEvents = [
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
];

const gaContentGroup = {
  site: 'contentGroup1',
  freeTrial: 'contentGroup2',
  intent: 'contentGroup3',
  userType: 'contentGroup4',
};

// Adwords ConversionTypes configured in Adwords
const conversionTypes = {
  freeTrial: '9dbhCIqug6UZEOOJ4ac9',
  signUp: '_lrzCMfsqqUZEOOJ4ac9',
};

export const disabledHubspotPaths = [
  'viewer',
  'profile.shared.file',
  'rfq',
  'order',
  'initiative.shared.file',
  'profile.project.file',
  'initiative.project.file',
  'intermediator.rfq',
  'intermediator.order',
  'intermediator.quote',
];

export const setHubspotVisible = visible => {
  if (!window.HubSpotConversations) return;

  if (visible) {
    window.HubSpotConversations.widget.load();
    window.HubSpotConversations.widget.close();
  } else {
    window.HubSpotConversations.widget.remove();
  }
};

class Analytics {
  @observable enabled = false;
  groupSlug = null;
  profile = null;
  constructor() {
    this.gaInitialized = false;
    this.sessionReplayTracking = null;
    this.amplitudeInitialized = false;
    try {
      this.debugEnabled = localStorage.getItem('debug') || false;
      const LSpolicy =
        JSON.parse(localStorage.getItem('acceptedPolicy')) || false;
      this.enabled = LSpolicy;
    } catch (e) {
      this.enabled = false;
    }
    localStorage.setItem('acceptedPolicy', JSON.stringify(this.enabled));
  }

  @action Enable = userId => {
    this.enabled = true;
    localStorage.setItem('acceptedPolicy', JSON.stringify(this.enabled));
    this.initialize(userId);
  };

  initialize(profile, routerStore) {
    const userId = profile?.username;
    const googleTagIds = [];
    if (GA4_TRACKING_ID) {
      googleTagIds.push({ trackingId: GA4_TRACKING_ID });
    }
    if (ADWORDS_ID) {
      googleTagIds.push({ trackingId: ADWORDS_ID });
    }

    if (googleTagIds.length > 0) {
      ReactGA.initialize(googleTagIds);
      ReactGA.set(gaContentGroup.site, site);
      const intent = profile?.intent?.name;
      const userType = profile?.userType?.name;
      if (intent) {
        ReactGA.set(gaContentGroup.intent, intent);
      }
      if (userType) {
        ReactGA.set(gaContentGroup.userType, userType);
      }
      this.gaInitialized = true;
    }

    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setTag('project', 'alpha');
        scope.setTag('service', 'frontend');
      });
    }

    if (userId) {
      this.linkUser(profile);
    }
    window.hsConversationsSettings = {
      loadImmediately: false,
    };
    // // Initialize hubspot:
    // if (window.HubSpotConversations) {
    //   this.onHubspotConversationsAPIReady(routerStore);
    // } else {
    //   window.hsConversationsOnReady = [
    //     () => this.onHubspotConversationsAPIReady(routerStore),
    //   ];
    // }
  }

  onHubspotConversationsAPIReady(routerStore) {
    const currentRoute = routerStore.route?.name;

    let visible = true;

    if (
      !currentRoute ||
      disabledHubspotPaths.includes(currentRoute) ||
      isInDesktopMode()
    ) {
      visible = false;
    }

    setHubspotVisible(visible);
  }

  linkAmplitudeGroup(groupSlug) {
    if (this.amplitudeInitialized) {
      amplitude.setGroup('workspace', groupSlug);
    }
    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[analytics] workspace: ${groupSlug}`);
    }
  }

  linkAmplitudeUser(userGlobalID, userSlug, userEmail, isBanned) {
    if (this.amplitudeInitialized) {
      amplitude.setUserId(userGlobalID);
      const identifyEvent = new amplitude.Identify();
      if (userSlug) {
        identifyEvent.set('slug', userSlug);
      }
      if (userEmail) {
        identifyEvent.set('email', userEmail);
      }
      if (typeof isBanned !== 'undefined') {
        identifyEvent.set('is_banned', isBanned);
      }
      amplitude.identify(identifyEvent);
      if (this.sessionReplayTracking === null) {
        let theSampleRate = 0.3; // a very small number between 0 and 1.
        if (typeof AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE !== 'undefined') {
          theSampleRate = Number(AMPLITUDE_SESSION_REPLAY_SAMPLE_RATE);
        }
        this.sessionReplayTracking = sessionReplayPlugin({
          sampleRate: theSampleRate, // 1 for 100% sample rate, should reduce for production traffic.
        });
        amplitude.add(this.sessionReplayTracking);
      }
    }
  }

  linkUser(profile) {
    if (!profile) {
      this.logError(
        new Error('Attempted to linkUser when no profile was provided'),
      );
      return;
    }

    this.profile = profile;

    const userId = profile.username;
    if (GA4_TRACKING_ID) {
      ReactGA.set({ userId });
      ReactGA.set(
        gaContentGroup.freeTrial,
        !!hasSpaceFreeTrial(profile.inSpace),
      );
    }

    if (GA4_TRACKING_ID && ADWORDS_ID) {
      // add 'user_data' for enhanced conversions
      // N.B. gtag is put on 'window' provided by the 'react-ga4' package
      window.gtag('set', 'user_data', {
        email: profile.user?.email,
        /*  We should do add the below data for enhanced conversions.
        Perhaps we can get this from stripe?
        Don't we now collect Phone data?

        When sending signup (and free trial) events we should set phone number
        immediately before sending.

        phone_number: yourPhoneVariable,
        address: {
          first_name: yourFirstNameVariable,
          last_name: yourLastNameVariable,
          street: yourStreetAddressVariable,
          city: yourCityVariable,
          region: yourRegionVariable,
          postal_code: yourPostalCodeVariable,
          country: yourCountryVariable,
        }, */
      });
    }

    if (AMPLITUDE_API_KEY) {
      amplitude.init(AMPLITUDE_API_KEY, {
        defaultTracking: {
          sessions: true,
        },
      });
      this.amplitudeInitialized = true;
      let isBanned = false;
      if (profile.management) {
        const userBannedInfo = JSON.parse(profile.management);
        isBanned = userBannedInfo.banned;
      }
      this.linkAmplitudeUser(
        profile.user?.id,
        userId,
        profile.user?.email,
        isBanned,
      );
    }

    if (window.newrelic) {
      window.newrelic.setUserId(userId);

      if (isInDesktopMode()) {
        window.newrelic.setCustomAttribute({
          appVersion: window.appConfig.getVersion(),
        });
      }
    }

    if (!isInDesktopMode()) {
      // Identify the user for Hubspot:
      // eslint-disable-next-line no-multi-assign
      const _hsq = (window._hsq = window._hsq || []);
      _hsq.push([
        'identify',
        {
          email: this.profile?.user?.email,
          username: this.profile?.username,
        },
      ]);
    }

    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setUser({ username: userId.replace('@', '') });
      });
    }

    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[analytics] linking user: ${userId}`);
    }
  }

  sendAdwordsConversion(type, product, val_in_eur) {
    /* Types must be configured in GoogleAds as Conversion Actions.
    Product is a "custom variable" in ads.
    val_in_eur is only needed for those where we choose the option:
     - "Use different values for each conversion".  */

    if (!(GA4_TRACKING_ID && ADWORDS_ID)) {
      console.warn('Google tracking not enabled');
      return false;
    }
    const payload = {
      send_to: `${ADWORDS_ID}/${conversionTypes[type]}`,
      // event_callback: callback,
    };
    if (val_in_eur !== undefined) {
      payload.value = val_in_eur;
      payload.currency = 'EUR';
    }
    if (product !== undefined) {
      payload.product = product;
    }
    window.gtag('event', 'conversion', payload);
    return false;
  }

  sendFbConversionEvent(event) {
    if (FACEBOOK_PIXEL_ID && FACEBOOK_CONVERSIONS_ACCESS_TOKEN) {
      const API_VERSION = 'v12.0';
      const xhr = new XMLHttpRequest();
      xhr.open(
        'POST',
        `https://graph.facebook.com/${API_VERSION}/${FACEBOOK_PIXEL_ID}/events?access_token=${FACEBOOK_CONVERSIONS_ACCESS_TOKEN}`,
        true,
      );
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(JSON.stringify(event));
    }
  }

  sendEvent(
    eventName,
    eventData = {},
    { ga = false, facebook = false, twitter = false, amp = false } = {
      ga: true,
      facebook: true,
      twitter: false,
      amp: false,
    },
  ) {
    const sendTo = { ga, facebook, twitter, amp };
    let patchedEventData = { ...eventData };
    let patchedEventName = eventName;
    if (sendTo.ga && GA4_TRACKING_ID) {
      if (!this.gaInitialized) {
        return;
      }

      switch (patchedEventName) {
        case 'modal-view':
          ReactGA.send({ hitType: 'pageview', page: patchedEventData.name });
          break;
        case 'page-view':
          ReactGA.send({ hitType: 'pageview', page: patchedEventData.url });
          break;
        default:
          ReactGA.event(patchedEventName, eventData);
          break;
      }
    }

    if (sendTo.amp && this.amplitudeInitialized) {
      amplitude.track(patchedEventName, patchedEventData);
    }

    patchedEventName =
      patchedEventName === 'page-view' ? 'PageView' : patchedEventName;
    if (sendTo.twitter && window.twq) {
      window.twq('track', patchedEventName, patchedEventData);
    }
    if (sendTo.facebook) {
      patchedEventData = {
        ...eventData,
        content_type: 'product',
        contents: [
          { ...this.profile?.userType },
          { ...this.profile?.intent },
        ].filter(e => !!e?.id),
      };

      if (window.fbq) {
        // For pixel legacy
        if (defaultFBEvents.includes(patchedEventName)) {
          window.fbq('track', patchedEventName, patchedEventData);
        } else {
          // Issue "CustomizeProduct" event on custom events
          patchedEventData.content_name = patchedEventName;

          patchedEventData.currency = patchedEventData.currency
            ? patchedEventData.currency
            : 'EUR';
          patchedEventData.value = patchedEventData.value
            ? patchedEventData.value
            : 2;

          window.fbq('track', 'CustomizeProduct', patchedEventData);
        }
      }

      const eventsForConvAPIFilter = [
        'CompleteRegistration',
        'SubmitApplication',
        'Schedule',
        'Donate',
        'Contact',
        'Lead',
      ];

      // Don't send the event to Conversions API if it is not a desired one
      if (eventsForConvAPIFilter.includes(patchedEventName)) {
        const now = new Date();
        const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);

        if (this.profile) {
          const mailHash = window.crypto.randomUUID();

          // Move the currency and value to inside the contents, so it is later sent as custom_data
          if (patchedEventData.currency) {
            patchedEventData.contents.push({
              currency: patchedEventData.currency,
            });
          }
          if (patchedEventData.value) {
            patchedEventData.contents.push({ value: patchedEventData.value });
          }

          const contentsAsObj = {};

          for (let i = 0; i < patchedEventData.contents.length; i += 1) {
            const entry = patchedEventData.contents[i];

            const keys = Object.keys(entry);
            for (let k = 0; k < keys.length; k += 1) {
              contentsAsObj[keys[k]] = entry[keys[k]];
            }
          }

          const conversionEvent = {
            data: [
              {
                event_name: patchedEventName,
                action_source: 'website',
                event_time: utc,
                custom_data: contentsAsObj,
                user_data: { em: [mailHash] },
              },
            ],
          };
          this.sendFbConversionEvent(conversionEvent);
        }
      }
    }

    // Send page-view events to our own server
    if (MODE !== 'development' && eventName === 'page-view') {
      fetch(
        `${BACKEND_URL_BASE}/stats/pageview?uip=browser&dp=${window.location.href}`,
      )
        .then(response => response)
        .then(() => {})
        .catch(console.warn);
    }

    // Send custom events to Facebook anyways
    if (MODE === 'development') {
      const services = Object.keys(sendTo).filter(service => sendTo[service]);
      // eslint-disable-next-line no-console
      console.log(
        `[analytics] sending event ${patchedEventName}, metadata: ${JSON.stringify(
          patchedEventData,
        )}, services: ${services}`,
      );
    }
  }

  linkedinPixel(conversionID) {
    const partnerID = window._linkedin_partner_id;
    if (!(document && partnerID && conversionID)) return;
    const container = document.querySelector('body');
    const pixel = document.createElement('img');
    pixel.src = `https://px.ads.linkedin.com/collect/?pid=${partnerID}&conversionId=${conversionID}&fmt=gif`;
    pixel.onload = () => {
      try {
        container.removeChild(pixel);
      } catch (err) {
        console.error(`Pixel ${conversionID} couldn't be removed`);
      }
    };
    container.appendChild(pixel);
  }

  logError(err, scopingFunction = () => {}) {
    if (!(err instanceof Error)) {
      console.error('Captured non-error', typeof err, ':', err);
      if (typeof err === 'string') {
        err = new Error(`Non-error:${err}`);
      } else if (err?.message) {
        err = new Error(`Non-error:${err.message}`);
      } else {
        return;
      }
    }

    if (window.newrelic) {
      const customAttributes = {};
      if (err.extensions?.code) {
        customAttributes.code = err.extensions.code;
        customAttributes['backend-service'] = err.extensions.serviceName;
        customAttributes.stack = err.stack;
      }
      window.newrelic.noticeError(err, customAttributes);
    }

    if (window.Sentry) {
      if (err.message === 'User not confirmed') {
        this.addBreadcrumb(err.message);
      } else {
        window.Sentry.withScope(scope => {
          scopingFunction(scope);
          if (err.extensions?.code) {
            scope.setTag('code', err.extensions.code);
            scope.setTag('backend-service', err.extensions.serviceName);
            scope.setTag('stack', err.stack);
            window.Sentry.captureException(new Error(err.message));
          } else {
            window.Sentry.captureException(err);
          }
        });
      }
    } else {
      console.warn('no sentry');
    }
    console.error(err);
  }

  addBreadcrumb(breadcrumb) {
    if (window.Sentry) {
      window.Sentry.addBreadcrumb(breadcrumb);
    }
  }

  logDebug(message) {
    if (this.debugEnabled) {
      // eslint-disable-next-line no-console
      console.log(`[***][DEBUG] ${message}`);
    }
  }

  unlinkUser() {
    this.profile = null;
    if (GA4_TRACKING_ID) ReactGA.set({ userId: null });
    if (window.Sentry) {
      window.Sentry.configureScope(scope => {
        scope.setUser();
      });
    }
    if (this.amplitudeInitialized) {
      if (this.sessionReplayTracking !== null) {
        amplitude.remove(this.sessionReplayTracking.name);
        this.sessionReplayTracking = null;
      }
      amplitude.reset();
    }

    if (window.newrelic) window.newrelic.setUserId(null);

    if (MODE === 'development') {
      // eslint-disable-next-line no-console
      console.log(`[analytics] unlinking user`);
    }
  }
}

export const analytics = new Analytics();
